import { useState } from "react";
import type { ReactNode } from "react";
import { useCopy } from "../../../shared/i18n";
import { AuthHeading } from "../../console/public/AuthSplit";
import {
  LockedPasswordInput,
  PasswordChecklist,
} from "../../console/public/SetPasswordParts";
import { LABEL, LINK_QUIET } from "../../console/public/styles";
import { passwordFormFailure, type PasswordFormFailure } from "../signInFlow";
import { passwordProblem, passwordRuleItems } from "./inviteModel";

/**
 * P3-16 — "choose a new password before you continue", the step a sign-in
 * reaches when `User.mustChangePassword` is set.
 *
 * 🔴 WHY A STEP OF THE SIGN-IN AND NOT A SCREEN BEHIND IT. The password just
 * typed was chosen by somebody else — a school admin who set it for a teacher,
 * or Kalqy's temporary password for a new admin (D10, D11) — so a session that
 * opened first and asked later would be a session on a credential two people
 * know. The server issues no tokens until this succeeds; the router holds a
 * purpose-scoped change token and this form holds two text boxes.
 *
 * ⚠️ SHARED BY THE TEACHER/SCHOOL DOORS AND THE OPERATOR DOOR, so the rule, the
 * checklist and the refusals cannot drift between two copies — the
 * `MfaEnrolment.tsx` precedent.
 */
export function ChangePasswordStep({
  idPrefix,
  onSubmit,
  onBack,
  submitClass,
  renderAlert,
}: {
  idPrefix: string;
  /**
   * Given the new password. Resolves once the router has acted on what came
   * back (signed in, wrong door, or the second factor); rejects with a
   * `status` the form turns into one of five sentences.
   */
  onSubmit: (newPassword: string) => Promise<void>;
  /** Back to the password form, discarding the change token. */
  onBack: () => void;
  submitClass: string;
  renderAlert: (children: ReactNode, testId?: string) => ReactNode;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<PasswordFormFailure | null>(null);

  const t = useCopy({
    en: {
      title: "Choose a new password",
      subtitle:
        "The password you just used was set by someone else. Choose your own before you continue.",
      password: "New password",
      confirm: "Confirm password",
      confirmPlaceholder: "Type it again",
      mismatch: "Those two do not match.",
      submit: "Save and continue",
      saving: "Saving…",
      back: "Start again",
      rule: "That password does not meet the rules above. Choose another.",
      /* One sentence for an expired step and a used token — the screen cannot
         tell them apart, and the fix for both is the same. */
      refused:
        "This sign-in took too long. Start again and sign in with the same password.",
      throttled: "Too many attempts from here. Wait a minute and try again.",
      serverFault:
        "Something is wrong at Kalqy’s end, and trying again will not fix it. Ask your school administrator or Kalqy support.",
      failed: "Couldn’t reach Kalqy. Check the connection and try again.",
    },
  });

  const problem = passwordProblem(password, confirm);
  const showMismatch = confirm.length > 0 && problem === "MISMATCH";

  async function submit(): Promise<void> {
    if (problem !== null || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      await onSubmit(password);
      /* No success state: the router replaces this screen, or the caller moves
         to the code step. Clearing here keeps nothing typed in state either way. */
      setPassword("");
      setConfirm("");
    } catch (err) {
      setFailure(
        passwordFormFailure((err as { status?: number } | null)?.status),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <AuthHeading title={t.title} subtitle={t.subtitle} />

      <label className={`${LABEL} mt-6`} htmlFor={`${idPrefix}-new-password`}>
        {t.password}
      </label>
      <div className="mt-2">
        <LockedPasswordInput
          id={`${idPrefix}-new-password`}
          autoComplete="new-password"
          autoFocus
          maxLength={200}
          value={password}
          aria-describedby={`${idPrefix}-new-password-rules`}
          onChange={(e) => {
            setPassword(e.target.value);
          }}
        />
      </div>
      <PasswordChecklist
        id={`${idPrefix}-new-password-rules`}
        items={passwordRuleItems(password)}
      />

      <label
        className={`${LABEL} mt-5`}
        htmlFor={`${idPrefix}-new-password-confirm`}
      >
        {t.confirm}
      </label>
      <div className="mt-2">
        <LockedPasswordInput
          id={`${idPrefix}-new-password-confirm`}
          autoComplete="new-password"
          maxLength={200}
          placeholder={t.confirmPlaceholder}
          value={confirm}
          aria-invalid={showMismatch || undefined}
          onChange={(e) => {
            setConfirm(e.target.value);
          }}
        />
      </div>
      {showMismatch ? (
        <p className="mt-1.5 text-[12px] leading-4 text-danger">{t.mismatch}</p>
      ) : null}

      <button
        type="submit"
        disabled={busy || problem !== null}
        aria-busy={busy || undefined}
        className={submitClass}
      >
        {busy ? t.saving : t.submit}
      </button>

      {failure === "rule" && renderAlert(t.rule, "change-password-rule")}
      {failure === "refused" &&
        renderAlert(t.refused, "change-password-refused")}
      {failure === "throttled" && renderAlert(t.throttled)}
      {failure === "server-fault" && renderAlert(t.serverFault)}
      {failure === "failed" && renderAlert(t.failed)}

      <p className="mt-5 text-center">
        <button type="button" onClick={onBack} className={LINK_QUIET}>
          {t.back}
        </button>
      </p>
    </form>
  );
}
